import i18n, { t } from 'i18next';
import { isEmpty } from 'lodash';

import { getCountryNameFromCode } from './CountryUtils';
import { Nl2br } from './Nl2br';

type Address = {
    name?: string;
    addressLine1?: string;
    zipCode?: string;
    city?: string;
    country?: string;
    countryCode?: string;
};

export function formatAddress(
    addressObject: Address,
    options: {
        showName?: boolean;
        showCountry?: boolean;
        fallbackWhenEmpty?: string;
    } = {}
) {
    const language = i18n.language;

    const optionsWithDefaults = {
        showName: true,
        showCountry: true,
        fallbackWhenEmpty: t('Not specified'),
        ...options,
    };

    if (!addressObject || isEmpty(addressObject)) {
        return optionsWithDefaults.fallbackWhenEmpty;
    }

    let formattedAddress = optionsWithDefaults.showName && addressObject.name ? `${addressObject.name} -` : '';

    if (addressObject.addressLine1) {
        formattedAddress = formattedAddress
            ? `${formattedAddress} ${addressObject.addressLine1}`
            : addressObject.addressLine1;
    }

    if (addressObject.city && addressObject.zipCode) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}, ${addressObject.zipCode} ${addressObject.city}`
            : `${addressObject.zipCode} ${addressObject.city}`;
    } else if (addressObject.city) {
        formattedAddress = formattedAddress ? `${formattedAddress}, ${addressObject.city}` : addressObject.city;
    } else if (addressObject.zipCode) {
        formattedAddress = formattedAddress ? `${formattedAddress}, ${addressObject.zipCode}` : addressObject.zipCode;
    }

    if (optionsWithDefaults.showCountry && (addressObject.countryCode || addressObject.country)) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}, ${getCountryNameFromCode(
                  addressObject.countryCode,
                  language,
                  addressObject.country
              )}`
            : getCountryNameFromCode(addressObject.countryCode, language, addressObject.country);
    }

    return formattedAddress !== '' ? formattedAddress : optionsWithDefaults.fallbackWhenEmpty;
}

export function formatAddressShort(
    addressObject: Address,
    options: {
        showName?: boolean;
        showCountry?: boolean;
        fallbackWhenEmpty?: string;
    } = {}
) {
    const language = i18n.language;

    const optionsWithDefaults = {
        showName: true,
        showCountry: false,
        fallbackWhenEmpty: t('Not specified'),
        ...options,
    };

    if (!addressObject || isEmpty(addressObject)) {
        return optionsWithDefaults.fallbackWhenEmpty;
    }

    if (optionsWithDefaults.showName && addressObject.name) {
        return addressObject.name;
    }

    let formattedAddress = '';
    if (addressObject.city && addressObject.zipCode) {
        formattedAddress = `${addressObject.zipCode} ${addressObject.city}`;
    } else if (addressObject.city) {
        formattedAddress = addressObject.city;
    } else if (addressObject.zipCode) {
        formattedAddress = addressObject.zipCode;
    }

    if (optionsWithDefaults.showCountry && (addressObject.countryCode || addressObject.country)) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}, ${getCountryNameFromCode(
                  addressObject.countryCode,
                  language,
                  addressObject.country
              )}`
            : getCountryNameFromCode(addressObject.countryCode, language, addressObject.country);
    }

    return formattedAddress !== '' ? formattedAddress : optionsWithDefaults.fallbackWhenEmpty;
}

export function formatMultilineAddress(addressObject: Address) {
    const language = i18n.language;

    if (!addressObject || isEmpty(addressObject)) return t('Not specified');

    const { city, country, countryCode, zipCode, addressLine1 } = addressObject;
    let formattedAddress = addressLine1 ?? '';

    if (zipCode) {
        formattedAddress = formattedAddress ? `${formattedAddress}\n${zipCode}` : `${zipCode}`;
    }
    if (city) {
        formattedAddress = zipCode ? `${formattedAddress}, ${city}` : `${formattedAddress}\n${city}`;
    }
    if (country || countryCode) {
        formattedAddress = formattedAddress
            ? `${formattedAddress}\n${getCountryNameFromCode(countryCode, language, country)}`
            : `${getCountryNameFromCode(countryCode, language, country)}`;
    }

    return formattedAddress !== '' ? Nl2br(formattedAddress) : t('Not specified');
}
